import { useState } from 'react'
import { BrowserRouter as Router, useNavigate, useLocation } from 'react-router-dom'
import { DashboardType } from '@/types'
import { OrdersDashboard } from '@/components/dashboards/OrdersDashboard'
import { SupportDashboard } from '@/components/dashboards/SupportDashboard'
import { PredictiveDashboard } from '@/components/dashboards/PredictiveDashboard'
import AssetManagementDashboard from '@/components/assets/AssetManagementDashboard'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import { 
  ShoppingCartIcon, 
  WrenchScrewdriverIcon, 
  ChartBarIcon, 
  LifebuoyIcon 
} from '@heroicons/react/24/outline'

function AppContent() {
  const navigate = useNavigate()
  const location = useLocation()
  const [selectedDashboard, setSelectedDashboard] = useState<DashboardType>(
    location.pathname === '/support' ? 'support' : 
    location.pathname === '/predictive' ? 'predictive' :
    location.pathname === '/assets' ? 'assets' : 'orders'
  )

  const handleDashboardChange = (dashboard: DashboardType) => {
    setSelectedDashboard(dashboard)
    navigate(dashboard === 'support' ? '/support' : 
             dashboard === 'predictive' ? '/predictive' :
             dashboard === 'assets' ? '/assets' : '/')
  }

  const navItems = [
    { id: 'orders', label: 'Orders', icon: ShoppingCartIcon },
    { id: 'assets', label: 'Assets', icon: WrenchScrewdriverIcon },
    { id: 'predictive', label: 'Predictive', icon: ChartBarIcon },
    { id: 'support', label: 'Support', icon: LifebuoyIcon }
  ] as const

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <div className="container mx-auto px-4 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-6">Dashboard</h1>
          <div className="flex flex-wrap gap-2">
            {navItems.map(({ id, label, icon: Icon }) => (
              <Button
                key={id}
                variant={selectedDashboard === id ? 'default' : 'outline'}
                onClick={() => handleDashboardChange(id as DashboardType)}
                className={cn(
                  'flex items-center gap-2 px-4 py-2 rounded-lg transition-all duration-200',
                  selectedDashboard === id 
                    ? 'bg-blue-600 text-white hover:bg-blue-700' 
                    : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                )}
              >
                <Icon className="h-5 w-5" />
                {label}
              </Button>
            ))}
          </div>
        </div>

        <div className={cn(
          'space-y-6',
          selectedDashboard === 'orders' ? 'block' : 'hidden'
        )}>
          <OrdersDashboard />
        </div>

        <div className={cn(
          'space-y-6',
          selectedDashboard === 'assets' ? 'block' : 'hidden'
        )}>
          <AssetManagementDashboard />
        </div>

        <div className={cn(
          'space-y-6',
          selectedDashboard === 'predictive' ? 'block' : 'hidden'
        )}>
          <PredictiveDashboard />
        </div>

        <div className={cn(
          'space-y-6',
          selectedDashboard === 'support' ? 'block' : 'hidden'
        )}>
          <SupportDashboard />
        </div>
      </div>
    </div>
  )
}

export default function App() {
  return (
    <Router>
      <AppContent />
    </Router>
  )
} 